// © Microsoft Corporation. All rights reserved.

import { ComponentConnector, UsersConnector } from "../../Social.props";
import { TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid } from "../../Social.telemetry";
import { User, UserList } from "@msnews/social-data-service";

import FollowButton from "../../follow-button/FollowButton";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { SocialState } from "../../Social.state";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import avatarFallback from "../../utils/avatarFallback";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import { enums } from "../../utils/enums";
import { hasThemeBase } from "../../utils/themeHelpers";
import loadObserver from "../../utils/ObserverHelper";
import openProfilePage from "../../utils/openProfilePage";
import { placeholderImages } from "../../utils/images";
import { profileUsersTelemetryContracts } from "./ProfileUsers.telemetry";

export interface ProfileUsersProps {
    componentConnector: ComponentConnector;
    user: User;
    t: SocialLocalizedStrings;
    users: UserList;
    managedClasses?: SocialClassNameContract;
    usersConnector: UsersConnector;
    fetchUsers: (userId: string) => void;
    market: string;
    mainTelemetryObject?: TelemetryObjectBase;
    theme?: string;
    responsiveClass: string;
}

const mapStateToProps = function(state: SocialState) {
    return {
        theme: state.theme
    };
};

export class ProfileUsers extends React.Component<ProfileUsersProps> {
    containerRef: React.RefObject<HTMLUListElement>;
    footerRef: React.RefObject<HTMLLIElement>;
    observer: IntersectionObserver;
    isLoading: boolean;
    listItemTelemetryObjectMap: Array<TelemetryObjectBase>;
    isLoaded: boolean;
    isObservingFooter: boolean;

    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.footerRef = React.createRef();
        loadObserver(() => {
            this.observer = new IntersectionObserver(this.footerInView.bind(this), {
                threshold: 1.0
            });
        });
        this.listItemTelemetryObjectMap = [];
        this.isLoaded = false;
    }

    async footerInView(entries) {
        if (entries[0].isIntersecting && !this.isLoading && this.props.users && this.props.users.hasMore) {
            this.isLoading = true;
            await this.props.fetchUsers(this.props.user.id);
            this.isLoading = false;
        }
    }

    componentWillUnmount() {
        this.footerRef.current && this.observer.unobserve(this.footerRef.current);
    }

    componentDidUpdate() {
        if (this.containerRef && this.containerRef.current && !this.isLoaded) {
            this.isLoaded = true;
            dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.containerRef.current);
        }

        if (this.footerRef.current && this.observer && !this.isObservingFooter) {
            this.isObservingFooter = true;
            this.footerRef.current && this.observer.observe(this.footerRef.current);
        }
    }

    render() {
        const { responsiveClass, user, t, users, managedClasses, market, componentConnector, usersConnector, mainTelemetryObject, theme } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        if (!user) { return null; }

        return (
            <ul
                className={classNames(
                    managedClasses.profileUsersContainer,
                    [managedClasses.profileUsersSuperAppContainer, isSuperApp],
                    responsiveClass
                )}
                ref={this.containerRef}
            >
                {
                    users.list.map((user)=>{
                        this.listItemTelemetryObjectMap[user.id] = addOrUpdateTelemetryObject(mainTelemetryObject, {...mainTelemetryObject.contract}, { id: user.traceId }, this.listItemTelemetryObjectMap[user.id]);
                        this.listItemTelemetryObjectMap[`nameLink-${user.id}-name`] = addOrUpdateTelemetryObject(mainTelemetryObject, {...profileUsersTelemetryContracts.nameLink}, { id: user.traceId, headline: user.name}, this.listItemTelemetryObjectMap[`nameLink-${user.id}-name`]);
                        this.listItemTelemetryObjectMap[`nameLink-${user.id}-avatar`] = addOrUpdateTelemetryObject(mainTelemetryObject, {...profileUsersTelemetryContracts.nameLink}, { id: user.traceId, headline: user.name}, this.listItemTelemetryObjectMap[`nameLink-${user.id}-avatar`]);

                        return (
                            <li key={user.id} className="cwt-profile-user" {...renderHybrid(this.listItemTelemetryObjectMap[user.id], true)}>
                                <a className="cwt-name cwt-link"
                                    {...openProfilePage(componentConnector, theme, user.id, market, "_blank")}
                                    {...renderHybrid(this.listItemTelemetryObjectMap[`nameLink-${user.id}-avatar`], false)}>
                                    <img
                                        alt={t.AriaLabelsProfilePicture}
                                        className="cwt-avatar cwt-avatar-md"
                                        src={user.avatarUrl || placeholderImages.avatar}
                                        onError={(e)=>{ avatarFallback(e); }}
                                    />
                                </a>
                                <div className="cwt-user-info">
                                    <a className="cwt-name cwt-link"
                                        {...openProfilePage(componentConnector, theme, user.id, market, "_blank")}
                                        {...renderHybrid(this.listItemTelemetryObjectMap[`nameLink-${user.id}-name`], false)}>
                                        {user.name}
                                    </a>
                                    <div className="cwt-stats">
                                        <div className="cwt-stat">{user.postsCount} {t.ProfileCommentsLbl}</div>
                                        <div className="cwt-stat">{user.followersCount} {t.ProfileFollowersLbl}</div>
                                        <div className="cwt-stat">{user.likesCount} {t.ProfileReactionsLbl}</div>
                                    </div>
                                </div>
                                <div className="cwt-end">
                                    { !isSuperApp && <FollowButton {...{userId: user.id, usersConnector, managedClasses, t, mainTelemetryObject}} /> }
                                </div>
                            </li>);
                    })
                }
                <li ref={this.footerRef}/>
            </ul>
        );
    }
}

export default connect(mapStateToProps)(ProfileUsers);